import React, { type FC, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
  Autocomplete,
  TextField,
} from '@mui/material';
import { ExpandMoreRounded, HelpRounded, MoreHorizRounded } from '@mui/icons-material';
import sx from 'mui-sx';

import Div from '../../../../shared/ui/Div';
import { type IHowToInviteStudents, type ILingoLiftManageClass } from '../../module/types';
import {
  type ILingoLiftDeleteManageClassStudent,
  ILingoLiftManageClassCollapseItemDropDownMenu,
} from '../../../../features/lingoLiftClasses';
import {
  usePatchLingoLiftGoogleClassRoomImportLLStudentsMutation,
  usePatchLingoLiftGoogleClassRoomImportLWStudentsMutation,
  usePostLingoLiftAddCoteacherMutation,
  useDeleteLingoLiftCoteacherMutation,
} from '../../api';
import { useSnackbar } from '../../../../shared/hooks/useSnackbar';

import ManageChildColumns from './ManageChildColumns';
import ManageChildItem from './ManageChildItem';
import ManageTeacherColumns from './ManageTeacherColumns';
import ManageTeacherItem from './ManageTeacherItem';
import styles from './styles';

interface ILingoLiftClassesManageClassCollapseItemProps extends ILingoLiftManageClass {
  onDeleteClass: (id: number) => void;
  onDeleteStudent: (value: ILingoLiftDeleteManageClassStudent) => void;
  setCurrentClassId: (value: number | null) => void;
  setCurrentStudentId: (value: number | null) => void;
  isListenwise?: boolean;
  howToInviteStudents: IHowToInviteStudents;
  possibleCoteachers?: Array<{ id: number; name: string }>;
  onAddCoteacher?: (classId: number, teacherId: number) => Promise<void>;
  currentTeacherId?: number;
  teacherItems?: Array<{
    id: number;
    firstName?: string;
    lastName?: string;
    email: string;
    role: string;
    classTeacherId?: number;
  }>;
}

interface ITeacherWithNameOrNames {
  id: number;
  name?: string;
  firstName?: string;
  lastName?: string;
  [key: string]: any;
}

interface ErrorResponse {
  response: {
    data: {
      error: string;
    };
  };
}

const getTeacherDisplayName = (teacher: unknown): string => {
  if (!teacher || typeof teacher !== 'object') return '';

  const teacherObj = teacher as ITeacherWithNameOrNames;

  if (teacherObj.name) return teacherObj.name;

  if (teacherObj.firstName || teacherObj.lastName) {
    return `${teacherObj.firstName || ''} ${teacherObj.lastName || ''}`.trim();
  }

  return '';
};

const LingoLiftClassesManageClassCollapseItem: FC<
  ILingoLiftClassesManageClassCollapseItemProps
> = props => {
  const {
    id,
    name,
    accessToken,
    studentItems,
    teacherItems = [],
    isGoogleClassroomClass,
    isCleverClass,
    isCurrentTeacherCoteacher,
    setCurrentStudentId,
    onDeleteStudent,
    isListenwise,
    howToInviteStudents,
    possibleCoteachers = [],
    onAddCoteacher,
    currentTeacherId,
  } = props;

  const { showSuccessNotification, showErrorNotification } = useSnackbar();

  const [expand, setExpand] = React.useState(false);
  const [coteacherModalOpen, setCoteacherModalOpen] = React.useState(false);
  const [selectedCoteacher, setSelectedCoteacher] = useState<string>('');
  const [isAddingCoteacher, setIsAddingCoteacher] = React.useState(false);
  const [isDeletingCoteacher, setIsDeletingCoteacher] = React.useState(false);

  const [onUpdateLLStudents, { isLoading: isUpdateLLStudentsLoading }] =
    usePatchLingoLiftGoogleClassRoomImportLLStudentsMutation();
  const [onUpdateLWStudents, { isLoading: isUpdateLWStudentsLoading }] =
    usePatchLingoLiftGoogleClassRoomImportLWStudentsMutation();
  const [addCoteacher, { isLoading: isAddCoteacherLoading }] =
    usePostLingoLiftAddCoteacherMutation();
  const [deleteCoteacher, { isLoading: isDeleteCoteacherLoading }] =
    useDeleteLingoLiftCoteacherMutation();

  const isCurrentTeacherPrimary = !isCurrentTeacherCoteacher;

  const onUpdateStudents = async () => {
    try {
      if (isListenwise) {
        await onUpdateLWStudents({
          schoolClassId: id,
        });
      } else {
        await onUpdateLLStudents({
          llSchoolClassId: id,
        });
      }
      showSuccessNotification(`Roster of ${name} updated`);
    } catch (error) {
      showErrorNotification((error as ErrorResponse).response.data.error);
    }
  };

  const handleOpenCoteacherModal = () => {
    if (isListenwise && possibleCoteachers.length > 0) {
      setCoteacherModalOpen(true);
    }
  };

  const handleCloseCoteacherModal = () => {
    setCoteacherModalOpen(false);
    setSelectedCoteacher('');
  };

  const handleCoteacherChange = (event: SelectChangeEvent) => {
    setSelectedCoteacher(event.target.value);
  };

  const handleAddCoteacher = async () => {
    if (!selectedCoteacher || !isListenwise) return;

    setIsAddingCoteacher(true);
    try {
      await addCoteacher({
        school_class_id: id,
        teacher_id: parseInt(selectedCoteacher)
      }).unwrap();

      showSuccessNotification('Co-teacher added successfully');
      handleCloseCoteacherModal();
      window.location.reload();
    } catch (error) {
      const errorMessage = error &&
        typeof error === 'object' &&
        'data' in error &&
        typeof error.data === 'object' &&
        error.data &&
        'error' in error.data
          ? error.data.error
          : 'Failed to add co-teacher';

      showErrorNotification(errorMessage);
    } finally {
      setIsAddingCoteacher(false);
    }
  };

  const handleDeleteCoteacher = async (classTeacherId: number) => {
    if (!isListenwise || !isCurrentTeacherPrimary) return;

    setIsDeletingCoteacher(true);
    try {
      await deleteCoteacher(classTeacherId).unwrap();
      showSuccessNotification('Co-teacher removed successfully');
    } catch (error) {
      const errorMessage = error &&
        typeof error === 'object' &&
        'data' in error &&
        typeof error.data === 'object' &&
        error.data &&
        'error' in error.data
          ? error.data.error
          : 'Failed to remove co-teacher';

      showErrorNotification(errorMessage);
    } finally {
      setIsDeletingCoteacher(false);
    }
  };

  const isExpands = studentItems.length > 0 || teacherItems.length > 1;
  return (
    <Div sx={styles.container}>
      <Accordion expanded={expand}>
        <AccordionSummary
          sx={styles.summary}
          expandIcon={
            isExpands && (
              <IconButton onClick={() => setExpand(prev => !prev)}>
                <ExpandMoreRounded />
              </IconButton>
            )
          }
          aria-controls="panel1-content"
        >
          <Grid container>
            <Grid
              xs={12}
              sm={3}
              item
              sx={sx(styles.column, {
                sx: styles.indentName,
                condition: !isExpands,
              })}
            >
              <Link
                href="#"
                sx={sx(styles.entityName, {
                  sx: styles.indentName,
                  condition: !isExpands,
                })}
              >
                {name}
              </Link>
            </Grid>
            <Grid xs={12} sm={4} item sx={styles.column}>
              <Typography
                sx={sx({
                  sx: styles.indent,
                  condition: !isExpands,
                })}
              >
                {studentItems.length} students
              </Typography>
            </Grid>
            {!isGoogleClassroomClass && !isCleverClass && (
              <Grid display="flex" alignItems="center" gap={1} xs={12} sm={4} item>
                <Tooltip
                  title={
                    <Div>
                      <Typography variant="h3" fontWeight={600}>
                        {howToInviteStudents.title}
                      </Typography>
                      <Divider sx={styles.divider} />
                      <Typography>{howToInviteStudents.beforeText}</Typography>
                      <Link sx={styles.link} target="_blank" href={howToInviteStudents.url}>
                        {howToInviteStudents.url}
                      </Link>{' '}
                      <Typography>{howToInviteStudents.afterText}</Typography>
                    </Div>
                  }
                  placement="top"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        background: 'rgba(97, 97, 97, 0.98)',
                      },
                    },
                  }}
                >
                  <HelpRounded sx={styles.questionIcon} />
                </Tooltip>
                <Div sx={styles.column}>
                  <Typography>
                    Class Code: <b>{accessToken}</b>
                  </Typography>
                  <Link
                    onClick={() => {
                      navigator.clipboard.writeText(accessToken);
                      showSuccessNotification('Class code copied to clipboard');
                    }}
                    sx={{
                      ...styles.entitySemiName,
                      ...styles.clipBoardButton,
                    }}
                  >
                    Copy Code
                  </Link>
                </Div>
              </Grid>
            )}
            {(isGoogleClassroomClass && isCurrentTeacherPrimary) && (
              <Grid xs={12} sm={4} item sx={styles.column}>
                <Div sx={styles.lineTitleWrapper}>
                  {!(isUpdateLLStudentsLoading || isUpdateLWStudentsLoading) && (
                    <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>You can</Typography>
                  )}
                  <Link onClick={onUpdateStudents} sx={styles.rosterLink}>
                    {isUpdateLLStudentsLoading || isUpdateLWStudentsLoading
                      ? 'Importing...'
                      : 'update roster from Google Classroom'}
                  </Link>
                </Div>
              </Grid>
            )}
            {isCurrentTeacherCoteacher && (
              <Grid xs={12} sm={4} item sx={styles.column}>
                <Typography sx={styles.coTeacherLabel}>CO-TEACHER</Typography>
              </Grid>
            )}
          </Grid>

          {isCurrentTeacherPrimary ? (
            <ILingoLiftManageClassCollapseItemDropDownMenu
              {...props}
              onAddCoteacher={handleOpenCoteacherModal}
              isListenwise={isListenwise}
              possibleCoteachers={possibleCoteachers}
            />
          ) : (
            <IconButton disabled>
              <MoreHorizRounded sx={{ ...styles.dotedIcon, opacity: 0 }} />
            </IconButton>
          )}
        </AccordionSummary>
        <AccordionDetails sx={styles.details}>
          {studentItems.length > 0 && (
            <>
              <ManageChildColumns
                isLoadingUpdateStudents={isUpdateLLStudentsLoading || isUpdateLWStudentsLoading}
                onUpdateStudents={() => void onUpdateStudents()}
              />
              {studentItems.map((studentItem, index) => (
                <ManageChildItem
                  key={index}
                  {...studentItem}
                  setCurrentStudentId={setCurrentStudentId}
                  isCurrentTeacherPrimary={isCurrentTeacherPrimary}
                  onDeleteStudent={() =>
                    onDeleteStudent({
                      ...(isListenwise ? { schoolClassId: id } : { llSchoolClassId: id }),
                      studentId: studentItem.id,
                    })
                  }
                />
              ))}
            </>
          )}

          {/* Teachers section */}
          {teacherItems.length > 1 && (
            <Div sx={{ marginTop: studentItems.length > 0 ? 4 : 0 }}>
              <ManageTeacherColumns />
              {teacherItems.map((teacherItem, index) => (
                <ManageTeacherItem
                  key={index}
                  {...teacherItem}
                  isPrimaryTeacher={teacherItem.role === 'Primary'}
                  isCurrentTeacherPrimary={isCurrentTeacherPrimary}
                  onDeleteTeacher={
                    teacherItem.role !== 'Primary' && isCurrentTeacherPrimary && teacherItem.classTeacherId
                      ? () => handleDeleteCoteacher(teacherItem.classTeacherId as number)
                      : undefined
                  }
                />
              ))}
            </Div>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Add Co-teacher Modal */}
      {isListenwise && (
        <Dialog open={coteacherModalOpen} onClose={handleCloseCoteacherModal} aria-labelledby="add-coteacher-dialog-title">
          <DialogTitle id="add-coteacher-dialog-title">Add Co-Teacher</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Co-teachers can create assignments and review student work. However, you are still the only teacher who can make rostering or setting changes to this class.
            </DialogContentText>
            <FormControl fullWidth margin="normal">
              <Autocomplete
                id="coteacher-autocomplete"
                options={Array.isArray(possibleCoteachers) ? possibleCoteachers : []}
                getOptionLabel={(option) => {
                  return getTeacherDisplayName(option);
                }}
                isOptionEqualToValue={(option, value) => {
                  return option?.id === value?.id;
                }}
                value={selectedCoteacher ? possibleCoteachers.find(teacher =>
                  teacher && teacher.id && teacher.id.toString() === selectedCoteacher) || null : null}
                onChange={(_, newValue) => {
                  if (newValue) {
                    handleCoteacherChange({
                      target: { value: newValue.id.toString() }
                    } as SelectChangeEvent);
                  } else {
                    handleCoteacherChange({
                      target: { value: '' }
                    } as SelectChangeEvent);
                  }
                }}
                disabled={isAddingCoteacher}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Teacher"
                    variant="outlined"
                  />
                )}
                renderOption={(props, option) => {
                  if (option && typeof option === 'object') {
                    return (
                      <li {...props} key={option.id} style={{ color: 'rgba(0, 0, 0, 0.87)' }}>
                        {getTeacherDisplayName(option)}
                      </li>
                    );
                  }
                  return null;
                }}
                noOptionsText="No teachers available"
                fullWidth
                ListboxProps={{
                  style: {
                    color: 'rgba(0, 0, 0, 0.87)'
                  }
                }}
              />
            </FormControl>
            <Link
              href="#"
              onClick={() => showSuccessNotification("Teachers must first be signed up for Listenwise AND enrolled in your school subscription. Then they will appear on the list as a teacher available for co-teaching.")}
              sx={{ display: 'block', marginTop: 2, fontSize: '0.875rem' }}
            >
              Don't see the teacher you're looking for?
            </Link>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseCoteacherModal}
              color="primary"
              disabled={isAddingCoteacher}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddCoteacher}
              color="primary"
              variant="contained"
              disabled={!selectedCoteacher || isAddingCoteacher}
            >
              {isAddingCoteacher ? 'Adding...' : 'Add Co-teacher'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Div>
  );
};

export default LingoLiftClassesManageClassCollapseItem;
